import { caseApiRequest, workflowApiRequest } from './apiClient';
import { getFileCategories, getSubFileCategories } from './criminalCaseRequests';
import type { ApiResult } from './types';
import type {
  DesignerCanvasStage,
  DesignerStageKind,
  SaveWorkflowTemplateInput,
  WorkflowEscalationConfig,
  WorkflowEscalationRecipientUser,
  WorkflowKpiConfig,
  WorkflowRoutingConfig,
  WorkflowStageConfigState,
  WorkflowTemplateDesignerLoadResult,
  SavedWorkflowStageRef,
  WorkflowTemplateUpdateHistoryItem,
  WorkflowValidationCheck,
  WorkflowValidationResult,
} from './workflowTypes';

export type {
  DesignerCanvasStage,
  DesignerStageKind,
  WorkflowRoutingConfig,
  WorkflowKpiConfig,
  WorkflowEscalationConfig,
  WorkflowStageConfigState,
  WorkflowTemplateDesignerLoadResult,
  WorkflowTemplateUpdateHistoryItem,
  WorkflowValidationCheck,
  WorkflowValidationResult,
  SaveWorkflowTemplateInput,
} from './workflowTypes';

export { getFileCategories, getSubFileCategories };
export type WorkflowTemplateRow = {
  id: string;
  code: string;
  name: string;
  caseType: string;
  version: string;
  createdBy: string;
  lastUpdated: string;
};

export type WorkflowVersionRow = {
  id: string;
  templateId: string;
  version: string;
  createdAt: string;
  createdBy: string;
  status: 'Active' | 'Archived';
};

export type WorkflowStage = {
  id: string;
  templateId: string;
  code: string;
  label: string;
  hint: string;
};

export type AllocationOfficerOption = {
  id: string;
  label: string;
};

export type LetterTemplateOption = {
  id: string;
  name: string;
};

export type SaveWorkflowTemplateResult = {
  workflowTemplateId: string;
  workflowCode?: string;
  versionNo?: string | number;
  status?: string;
  statusCode?: string;
  isNewVersion?: boolean;
  isNewTemplate?: boolean;
  draftStatus?: boolean;
  stages?: SavedWorkflowStageRef[];
};

export type SaveWorkflowTemplateApiResult =
  | { success: true; data: SaveWorkflowTemplateResult }
  | { success: false; message: string; needsConfirmReplace?: boolean; allocationOfficerSaveFailed?: boolean };

export type LatestWorkflowVersionResult = {
  subFileCategoryId: string;
  workflowTemplateName: string;
  versionNo: number | null;
};

// ─── Mock list data (Workflow Templates list screen) ─────────────────────────

const TEMPLATES: WorkflowTemplateRow[] = [
  {
    id: 'wf-001',
    code: 'WF-001',
    name: 'Criminal Case Processing',
    caseType: 'Criminal',
    version: 'v1.2',
    createdBy: 'Mr. Nimal Silva',
    lastUpdated: '2023-10-12 10:30',
  },
  {
    id: 'wf-002',
    code: 'WF-002',
    name: 'Civil Dispute Resolution',
    caseType: 'Civil',
    version: 'v2.0',
    createdBy: 'Mr. Kamal Perera',
    lastUpdated: '2023-10-10 14:15',
  },
];

const VERSIONS: WorkflowVersionRow[] = [
  { id: 'ver-001', templateId: 'wf-001', version: 'v1.2', createdAt: '2023-10-12 10:30', createdBy: 'Mr. Nimal Silva', status: 'Active' },
];

const STAGES: WorkflowStage[] = [
  { id: 'st-1', templateId: 'wf-001', code: 'Stage 1', label: 'Register Request & Generate Cover Note', hint: '' },
  { id: 'st-2', templateId: 'wf-001', code: 'Stage 2', label: 'QR Check-in at Record Room', hint: '' },
];

export function listWorkflowTemplates() {
  return TEMPLATES;
}

export function listWorkflowVersions(templateId: string) {
  return VERSIONS.filter((v) => v.templateId === templateId);
}

export function listWorkflowStages(templateId: string) {
  return STAGES.filter((s) => s.templateId === templateId);
}


export function createDefaultDesignerStages(): DesignerCanvasStage[] {
  return [
    { id: 'sys-allocation', label: 'Allocation Officer Assignment', kind: 'allocation' },
    { id: 'sys-closure', label: 'Closure', kind: 'closure' },
    { id: 'sys-return', label: 'Return to Allocation Officer', kind: 'return' },
  ];
}

export function getNextVersionNumber(templateName: string): number {
  const normalized = templateName.trim().toLowerCase();
  if (!normalized) return 1;
  const count = TEMPLATES.filter((t) => t.name.trim().toLowerCase() === normalized).length;
  return count + 1;
}

// ─── API helpers ─────────────────────────────────────────────────────────────

function workflowAppCode(): string {
  return (import.meta.env.VITE_APP_CODE_WORKFLOW ?? 'aa').trim();
}

function workflowDetailsAppCode(): string {
  return (import.meta.env.VITE_APP_CODE_WORKFLOW_DETAILS ?? 'a').trim();
}

/** WF-{subCategoryCode}-{seq}-{version}, e.g. WF-CRM-001-1 */
export function buildWorkflowCode(
  subCategoryCode: string,
  existingCount: number,
  version?: string | number | null,
): string {
  // Sanitize: replace underscores with hyphens so codes from categories like
  // PUBLIC_PETITION stay valid (backend allows only alphanumerics and hyphens).
  const code = (subCategoryCode.trim() || 'GEN').toUpperCase().replace(/_/g, '-');
  const seq = String(Math.max(0, existingCount) + 1).padStart(3, '0');
  const base = `WF-${code}-${seq}`;
  if (version == null || version === '') return base;
  const versionLabel = String(version).trim().replace(/^v/i, '');
  return versionLabel ? `${base}-${versionLabel}` : base;
}

/** Sequence base for generated codes — new templates use full count; edits exclude the current row. */
export function workflowCodeSequenceBase(templateCount: number, isNewTemplate: boolean): number {
  if (isNewTemplate) return Math.max(0, templateCount);
  return Math.max(0, templateCount - 1);
}

export function isDraftWorkflowTemplateDetail(item: WorkflowTemplateDetailItem): boolean {
  return item.status === 'Draft';
}

/** Non-draft rows from the details API — used only for workflow code generation. */
export function workflowTemplateDetailsForCodeGeneration(
  items: WorkflowTemplateDetailItem[],
): WorkflowTemplateDetailItem[] {
  return items.filter((item) => !isDraftWorkflowTemplateDetail(item));
}

export function countWorkflowTemplatesForCodeGeneration(items: WorkflowTemplateDetailItem[]): number {
  return workflowTemplateDetailsForCodeGeneration(items).length;
}

function workflowCodeCategoryPrefix(codeSource: string): string {
  const code = (codeSource.trim() || 'GEN').toUpperCase().replace(/_/g, '-');
  return `WF-${code}`;
}

/** Highest 3-digit sequence already used in codes like WF-MLA-005-1 for the same category prefix. */
export function maxWorkflowCodeSequenceInUse(
  existingWorkflowCodes: string[],
  codeSource: string,
): number {
  const prefix = `${workflowCodeCategoryPrefix(codeSource)}-`;
  let maxSeq = 0;

  for (const raw of existingWorkflowCodes) {
    const normalized = raw.trim().toUpperCase();
    if (!normalized.startsWith(prefix)) continue;

    const rest = normalized.slice(prefix.length);
    const match = rest.match(/^(\d{3})(?:-\d+)?$/);
    if (match) {
      maxSeq = Math.max(maxSeq, Number.parseInt(match[1], 10));
    }
  }

  return maxSeq;
}

export function collectExistingWorkflowCodes(
  items: WorkflowTemplateDetailItem[],
  excludeTemplateId?: string,
): string[] {
  const exclude = excludeTemplateId?.trim();
  const codes: string[] = [];

  for (const item of workflowTemplateDetailsForCodeGeneration(items)) {
    if (exclude && item.id === exclude) continue;
    const code = item.workflowCode?.trim();
    if (code) codes.push(code);
  }

  return codes;
}

export function resolveGeneratedWorkflowCode(input: {
  subFileCategoryCode: string;
  fileCategoryCode?: string;
  isExemptCategory?: boolean;
  templateCount: number;
  isNewTemplate: boolean;
  versionNo?: string | number | null;
  existingWorkflowCodes?: string[];
}): string {
  const subCode = input.subFileCategoryCode.trim();
  const codeSource =
    subCode || (input.isExemptCategory ? (input.fileCategoryCode ?? '').trim() : '');
  const existing = new Set(
    (input.existingWorkflowCodes ?? [])
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean),
  );
  const versionNo = input.versionNo ?? '1';

  let sequenceBase = workflowCodeSequenceBase(input.templateCount, input.isNewTemplate);
  const maxSeqInUse = maxWorkflowCodeSequenceInUse(input.existingWorkflowCodes ?? [], codeSource);
  if (maxSeqInUse > 0) {
    sequenceBase = Math.max(sequenceBase, maxSeqInUse);
  }

  const maxAttempts = Math.max(existing.size, 1) + 1000;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const candidate = buildWorkflowCode(codeSource, sequenceBase, versionNo);
    if (!existing.has(candidate.toUpperCase())) {
      return candidate;
    }
    sequenceBase++;
  }

  return buildWorkflowCode(codeSource, sequenceBase, versionNo);
}

export function isWorkflowCodeUniquenessFailure(check: WorkflowValidationCheck): boolean {
  if (check.passed) return false;
  if (check.key === 'WORKFLOW_CODE') return true;
  return /workflow code must be unique/i.test(check.label);
}

/** Retry validate when the first call persisted a draft id but still failed code uniqueness. */
export function shouldRetryWorkflowCodeValidation(
  result: WorkflowValidationResult,
  input: SaveWorkflowTemplateInput,
): boolean {
  if (result.isNewTemplate === true) return false;
  if (!input.workflowCode?.trim()) return false;
  if (!result.checks.some(isWorkflowCodeUniquenessFailure)) return false;
  if (input.workflowTemplateId?.trim()) return false;
  return Boolean(result.workflowTemplateId?.trim());
}

/** null version from API → display 1; otherwise display version + 1 */
export function displayNextVersionNo(versionNo: number | null | undefined): string {
  if (versionNo == null || Number.isNaN(versionNo)) return '1';
  return String(versionNo + 1);
}

function strField(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function asArray(raw: unknown): unknown[] {
  return Array.isArray(raw) ? raw : [];
}

function numField(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

function nestedId(obj: unknown): string {
  if (!obj || typeof obj !== 'object') return '';
  return strField(obj as Record<string, unknown>, 'id');
}

function nestedName(obj: unknown): string {
  if (!obj || typeof obj !== 'object') return '';
  return strField(obj as Record<string, unknown>, 'name');
}

function normalizeWorkflowStatusName(name: string): 'Draft' | 'Active' | 'Inactive' {
  const s = name.trim().toLowerCase();
  if (s === 'active') return 'Active';
  if (s === 'inactive' || s === 'submitted') return 'Inactive';
  return 'Draft';
}

function stageKindFromName(name: string, index: number, total: number): DesignerStageKind {
  const n = name.trim().toLowerCase();
  if (n === 'allocation officer assignment') return 'allocation';
  if (n === 'return to allocation officer') return 'return';
  if (n === 'closure') return 'closure';
  if (index === 0) return 'allocation';
  if (index === total - 1) return 'return';
  if (index === total - 2) return 'closure';
  return 'custom';
}

function collectRevertStageIds(raw: unknown): string[] {
  const ids: string[] = [];

  const pushId = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) ids.push(value.trim());
  };

  const pushKeys = (value: unknown) => {
    if (!Array.isArray(value)) return;
    for (const key of value) pushId(key);
  };

  const pushRule = (item: Record<string, unknown>) => {
    pushKeys(item.allowed_revert_stage_names ?? item.allowedRevertStageKeys);
    pushKeys(item.revert_workflow_template_stage_ids ?? item.revertWorkflowTemplateStageIds);
    pushId(item.revert_workflow_template_stage_id ?? item.revertWorkflowTemplateStageId);

    const nested = item.revert_workflow_template_stage ?? item.revertWorkflowTemplateStage
      ?? item.revertToStage ?? item.revert_to_stage;
    if (nested && typeof nested === 'object') {
      pushId(
        strField(
          nested as Record<string, unknown>,
          'id',
          'workflow_template_stage_id',
          'workflowTemplateStageId',
        ),
      );
    }
  };

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    pushRule(raw as Record<string, unknown>);
  }

  for (const item of asArray(raw)) {
    if (!item || typeof item !== 'object') continue;
    pushRule(item as Record<string, unknown>);
  }

  return [...new Set(ids)];
}

function normalizeStageRouting(raw: unknown): WorkflowRoutingConfig | undefined {
  const allowedRevertStageKeys = collectRevertStageIds(raw);
  if (!allowedRevertStageKeys.length) return undefined;
  return { allowedRevertStageKeys };
}

function kpiTimeUnitIdFromApiField(
  r: Record<string, unknown>,
  idKeys: string[],
  objectKeys: string[],
): string | null {
  for (const key of idKeys) {
    const v = r[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  for (const key of objectKeys) {
    const nested = r[key];
    if (!nested) continue;
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
    const id = nestedId(nested);
    if (id) return id;
  }
  return null;
}

function normalizeStageKpi(raw: unknown): WorkflowKpiConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const ttc = numField(r, 'ttc');
  const ttr = numField(r, 'ttr');
  const tte = numField(r, 'tte');
  const ttcTimeUnitId =
    kpiTimeUnitIdFromApiField(r, ['ttc_time_unit_id', 'ttcTimeUnitId'], ['ttcTimeUnit', 'ttc_time_unit']) || null;
  const ttrTimeUnitId =
    kpiTimeUnitIdFromApiField(r, ['ttr_time_unit_id', 'ttrTimeUnitId'], ['ttrTimeUnit', 'ttr_time_unit']) || null;
  const tteTimeUnitId =
    kpiTimeUnitIdFromApiField(r, ['tte_time_unit_id', 'tteTimeUnitId'], ['tteTimeUnit', 'tte_time_unit']) || null;
  const hasValue = [ttc, ttr, tte].some((v) => v != null && v > 0);
  const hasUnits = Boolean(ttcTimeUnitId || ttrTimeUnitId || tteTimeUnitId);
  if (!hasValue && !hasUnits) return undefined;
  return {
    ttc: ttc != null && ttc > 0 ? ttc : null,
    ttr: ttr != null && ttr > 0 ? ttr : null,
    tte: tte != null && tte > 0 ? tte : null,
    ttcTimeUnitId: ttc != null && ttc > 0 ? ttcTimeUnitId : null,
    ttrTimeUnitId: ttr != null && ttr > 0 ? ttrTimeUnitId : null,
    tteTimeUnitId: tte != null && tte > 0 ? tteTimeUnitId : null,
  };
}

function kpiNumberForSave(value: number | null | undefined): number | null {
  if (value == null || value <= 0) return null;
  return value;
}

function kpiUnitIdForSave(value: number | null | undefined, unitId: string | null | undefined): string | null {
  if (value == null || value <= 0) return null;
  const id = unitId?.trim();
  return id || null;
}

function extractEscalationRecipientUser(raw: unknown): WorkflowEscalationRecipientUser | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = strField(row, 'id', 'userId', 'user_id');
  if (!id) return null;
  const name = strField(row, 'name', 'displayName', 'display_name', 'recipientName', 'recipient_name');
  return { id, name: name || id };
}

function collectEscalationRecipientUsers(raw: Record<string, unknown>): WorkflowEscalationRecipientUser[] {
  const users: WorkflowEscalationRecipientUser[] = [];
  const seen = new Set<string>();

  const pushUser = (rawUser: unknown) => {
    const parsed = extractEscalationRecipientUser(rawUser);
    if (!parsed || seen.has(parsed.id)) return;
    seen.add(parsed.id);
    users.push(parsed);
  };

  for (const level of asArray(raw.levels)) {
    if (!level || typeof level !== 'object') continue;
    for (const user of asArray((level as Record<string, unknown>).users)) {
      pushUser(user);
    }
  }

  for (const user of asArray(raw.users ?? raw.escalationUsers ?? raw.escalation_users)) {
    pushUser(user);
  }

  return users;
}

function normalizeStageEscalation(raw: unknown): WorkflowEscalationConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;

  const idsRaw = r.escalation_recipient_user_ids ?? r.escalationRecipientUserIds;
  const escalationRecipientUserIds: string[] = Array.isArray(idsRaw)
    ? idsRaw.filter((id): id is string => typeof id === 'string' && id.trim() !== '')
    : [];

  const escalationRecipientUsers = collectEscalationRecipientUsers(r);
  for (const user of escalationRecipientUsers) {
    if (!escalationRecipientUserIds.includes(user.id)) {
      escalationRecipientUserIds.push(user.id);
    }
  }

  if (!escalationRecipientUserIds.length) return undefined;

  return {
    escalationRecipientUserIds,
    escalationRecipientUsers: escalationRecipientUsers.length ? escalationRecipientUsers : undefined,
  };
}

function buildStageConfigFromApiStage(stage: Record<string, unknown>): WorkflowStageConfigState {
  const cfg: WorkflowStageConfigState = {
    routingConfigured: false,
    kpiConfigured: false,
    escalationConfigured: false,
  };

  const routing = normalizeStageRouting(stage.routingRules ?? stage.routing_rules);
  const routingRules = asArray(stage.routingRules ?? stage.routing_rules);
  if (routing && (routing.allowedRevertStageKeys.length > 0 || routingRules.length > 0)) {
    cfg.routingConfigured = true;
    cfg.routing = routing;
  }

  const kpi = normalizeStageKpi(stage.kpi ?? stage.kpis);
  if (kpi) {
    cfg.kpiConfigured = true;
    cfg.kpis = kpi;
  }

  const escalation = normalizeStageEscalation(
    stage.escalationRule ?? stage.escalation_rule ?? stage.escalationRules ?? stage.escalation_rules,
  );
  if (escalation) {
    cfg.escalationConfigured = true;
    cfg.escalation = escalation;
  }

  const roles = asArray(stage.assignedRoles ?? stage.assigned_roles);
  if (roles.length > 0 && roles[0] && typeof roles[0] === 'object') {
    const roleId = numField(roles[0] as Record<string, unknown>, 'id', 'role_id', 'roleId');
    if (roleId != null) cfg.closureRoleId = roleId;
  }

  return cfg;
}

function normalizeUpdateHistory(raw: unknown): WorkflowTemplateUpdateHistoryItem[] {
  return asArray(raw)
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const r = item as Record<string, unknown>;
      return {
        action: strField(r, 'action'),
        timestamp: strField(r, 'timestamp'),
        performedBy: strField(r, 'performedBy', 'performed_by'),
        performedByName: strField(r, 'performedByName', 'performed_by_name'),
      };
    })
    .filter((x): x is WorkflowTemplateUpdateHistoryItem => Boolean(x && (x.timestamp || x.performedByName)));
}

export function normalizeWorkflowTemplateDesignerLoad(raw: unknown): WorkflowTemplateDesignerLoadResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  const id = strField(r, 'id', 'workflow_template_id', 'workflowTemplateId');
  if (!id) return null;

  const versionRaw = r.version ?? r.version_no ?? r.versionNo;
  let versionNo = '1';
  if (typeof versionRaw === 'number' && Number.isFinite(versionRaw)) versionNo = String(versionRaw);
  else if (typeof versionRaw === 'string' && versionRaw.trim()) versionNo = versionRaw.trim().replace(/^v/i, '');

  const statusName = nestedName(r.status) || strField(r, 'status');
  const stagesRaw = asArray(r.stages);
  const sorted = [...stagesRaw].sort((a, b) => {
    const sa =
      a && typeof a === 'object'
        ? numField(
            a as Record<string, unknown>,
            'stage_order',
            'stageOrder',
            'sequenceNumber',
            'sequence_number',
          ) ?? 0
        : 0;
    const sb =
      b && typeof b === 'object'
        ? numField(
            b as Record<string, unknown>,
            'stage_order',
            'stageOrder',
            'sequenceNumber',
            'sequence_number',
          ) ?? 0
        : 0;
    return sa - sb;
  });

  const total = sorted.length;
  const canvasStages: DesignerCanvasStage[] = [];
  const stageConfigs: Record<string, WorkflowStageConfigState> = {};

  for (let index = 0; index < sorted.length; index += 1) {
    const item = sorted[index];
    if (!item || typeof item !== 'object') continue;
    const st = item as Record<string, unknown>;
    const stageId = strField(st, 'id', 'stage_id', 'stageId', 'workflow_template_stage_id');
    const stageName = strField(st, 'name', 'stage_name', 'stageName') || `Stage ${index + 1}`;
    if (!stageId) continue;

    const kind = stageKindFromName(stageName, index, total);
    canvasStages.push({ id: stageId, label: stageName, kind, backendStageId: stageId });
    stageConfigs[stageId] = buildStageConfigFromApiStage(st);
  }

  if (!canvasStages.length) {
    canvasStages.push(...createDefaultDesignerStages());
  }

  return {
    workflowTemplateId: id,
    workflowTemplateName: strField(r, 'name', 'workflow_template_name', 'workflowTemplateName'),
    workflowCode: strField(r, 'code', 'workflow_code', 'workflowCode'),
    versionNo,
    status: normalizeWorkflowStatusName(statusName),
    fileCategoryId: nestedId(r.fileCategory ?? r.file_category),
    subFileCategoryId: nestedId(r.subFileCategory ?? r.sub_file_category),
    allocationOfficerId: nestedId(r.allocationOfficer ?? r.allocation_officer),
    letterTemplateIds: normalizeLetterTemplateIds(r),
    canvasStages,
    stageConfigs,
    updateHistory: normalizeUpdateHistory(r.updateHistory ?? r.update_history),
  };
}

function normalizeAllocationOfficers(raw: unknown): AllocationOfficerOption[] {
  const rows = asArray(raw);
  const out: AllocationOfficerOption[] = [];
  for (const item of rows) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const id = strField(r, 'user_id', 'userId', 'id', 'allocation_officer_id', 'allocationOfficerId');
    const name = strField(r, 'display_name', 'displayName', 'name', 'full_name', 'fullName');
    const division = strField(r, 'division', 'division_name', 'divisionName');
    if (!id || !name) continue;
    const label = division ? `${name} (${division})` : name;
    out.push({ id, label });
  }
  return out;
}

function normalizeLetterTemplateIds(r: Record<string, unknown>): string[] {
  const fromArray = r.letter_template_ids ?? r.letterTemplateIds;
  if (Array.isArray(fromArray)) {
    const ids: string[] = [];
    for (const item of fromArray) {
      if (typeof item === 'string' && item.trim()) ids.push(item.trim());
      else if (typeof item === 'number' && Number.isFinite(item)) ids.push(String(item));
      else if (item && typeof item === 'object') {
        const row = item as Record<string, unknown>;
        const id =
          nestedId(row) ||
          strField(row, 'letter_template_id', 'letterTemplateId', 'id');
        if (id) ids.push(id);
      }
    }
    if (ids.length) return ids;
  }

  const templates = r.letter_templates ?? r.letterTemplates;
  if (Array.isArray(templates)) {
    const ids: string[] = [];
    for (const item of templates) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const id =
        nestedId(row) ||
        strField(row, 'letter_template_id', 'letterTemplateId', 'id');
      if (id) ids.push(id);
    }
    if (ids.length) return ids;
  }

  const single =
    nestedId(r.letterTemplate ?? r.letter_template) ||
    strField(r, 'letter_template_id', 'letterTemplateId');
  return single ? [single] : [];
}

function normalizeLetterTemplates(raw: unknown): LetterTemplateOption[] {
  const rows = asArray(raw);
  const out: LetterTemplateOption[] = [];
  for (const item of rows) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const id = strField(r, 'letter_template_id', 'letterTemplateId', 'id');
    const name = strField(r, 'name', 'letter_template_name', 'letterTemplateName', 'title');
    if (!id || !name) continue;
    out.push({ id, name });
  }
  return out;
}

function normalizeSavedStageRef(raw: unknown): SavedWorkflowStageRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const stageId = strField(
    r,
    'stage_id',
    'stageId',
    'id',
    'workflow_template_stage_id',
    'workflowTemplateStageId',
  );
  if (!stageId) return null;
  return {
    stageId,
    stageKey: strField(r, 'stage_key', 'stageKey'),
    stageOrder:
      numField(r, 'stage_order', 'stageOrder', 'sequence_number', 'sequenceNumber') ?? 0,
    stageName: strField(r, 'stage_name', 'stageName', 'name'),
  };
}

function normalizeSaveWorkflowResponse(raw: unknown): SaveWorkflowTemplateResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const workflowTemplateId = strField(
    r,
    'workflow_template_id',
    'workflowTemplateId',
    'fe_version_id',
    'feVersionId',
    'id',
    'template_id',
    'templateId',
  );
  if (!workflowTemplateId) return null;
  const versionRaw = r.version_no ?? r.versionNo ?? r.version;
  const stages = asArray(r.stages)
    .map(normalizeSavedStageRef)
    .filter((stage): stage is SavedWorkflowStageRef => Boolean(stage));
  const isNewVersionRaw = r.is_new_version ?? r.isNewVersion;
  const isNewTemplateRaw = r.is_new_template ?? r.isNewTemplate;
  const draftStatusRaw = r.is_draft_status ?? r.isDraftStatus ?? r.draftStatus;
  const statusCode = strField(r, 'status_code', 'statusCode') || undefined;

  return {
    workflowTemplateId,
    workflowCode: strField(r, 'workflow_code', 'workflowCode', 'code') || undefined,
    versionNo:
      typeof versionRaw === 'number' || typeof versionRaw === 'string' ? versionRaw : undefined,
    status:
      strField(r, 'status', 'workflow_status', 'workflowStatus') ||
      statusCode ||
      undefined,
    statusCode,
    isNewVersion: typeof isNewVersionRaw === 'boolean' ? isNewVersionRaw : undefined,
    isNewTemplate: typeof isNewTemplateRaw === 'boolean' ? isNewTemplateRaw : undefined,
    draftStatus: typeof draftStatusRaw === 'boolean' ? draftStatusRaw : undefined,
    stages: stages.length ? stages : undefined,
  };
}

function nextStageName(stages: DesignerCanvasStage[], stageId: string): string {
  const idx = stages.findIndex((s) => s.id === stageId);
  const next = stages[idx + 1];
  return next?.label.trim() ?? '';
}

function revertKeysToStageNames(stages: DesignerCanvasStage[], keys: string[]): string[] {
  const names: string[] = [];
  for (const key of keys) {
    const trimmed = key.trim();
    if (!trimmed) continue;
    const byId = stages.find((s) => s.id === trimmed);
    if (byId) {
      names.push(byId.label.trim());
      continue;
    }
    const byLabel = stages.find((s) => s.label.trim() === trimmed);
    names.push(byLabel?.label.trim() ?? trimmed);
  }
  return [...new Set(names)];
}

const API_STAGE_KEY_TO_KIND: Record<string, DesignerStageKind> = {
  allocation_officer_assignment: 'allocation',
  closure: 'closure',
  return_to_allocation_officer: 'return',
};

function savedStageKind(saved: SavedWorkflowStageRef, index: number, total: number): DesignerStageKind {
  const fromKey = API_STAGE_KEY_TO_KIND[saved.stageKey.trim().toLowerCase()];
  if (fromKey) return fromKey;
  return stageKindFromName(saved.stageName, index, total);
}

function remapStageConfigKeys(
  stageConfigs: Record<string, WorkflowStageConfigState>,
  stageIdMap: Record<string, string>,
): Record<string, WorkflowStageConfigState> {
  if (!Object.keys(stageIdMap).length) return stageConfigs;

  const next: Record<string, WorkflowStageConfigState> = {};
  for (const [oldId, cfg] of Object.entries(stageConfigs)) {
    const newId = stageIdMap[oldId] ?? oldId;
    const remappedCfg: WorkflowStageConfigState = { ...cfg };
    if (cfg.routing?.allowedRevertStageKeys?.length) {
      remappedCfg.routing = {
        ...cfg.routing,
        allowedRevertStageKeys: cfg.routing.allowedRevertStageKeys.map((key) => stageIdMap[key] ?? key),
      };
    }
    next[newId] = remappedCfg;
  }
  return next;
}

/** Reconcile canvas stage ids with stage ids returned after save/update. */
export function syncDesignerStagesFromSavedStages(
  canvasStages: DesignerCanvasStage[],
  stageConfigs: Record<string, WorkflowStageConfigState>,
  savedStages: SavedWorkflowStageRef[],
): {
  canvasStages: DesignerCanvasStage[];
  stageConfigs: Record<string, WorkflowStageConfigState>;
  stageIdMap: Record<string, string>;
} {
  if (!savedStages.length) {
    return { canvasStages, stageConfigs, stageIdMap: {} };
  }

  const sortedSaved = [...savedStages].sort((a, b) => a.stageOrder - b.stageOrder);
  const stageIdMap: Record<string, string> = {};

  const applySavedStage = (stage: DesignerCanvasStage, saved: SavedWorkflowStageRef): DesignerCanvasStage => {
    if (saved.stageId !== stage.id) stageIdMap[stage.id] = saved.stageId;
    return {
      ...stage,
      id: saved.stageId,
      backendStageId: saved.stageId,
      label: saved.stageName || stage.label,
    };
  };

  let nextCanvasStages: DesignerCanvasStage[];

  if (sortedSaved.length === canvasStages.length) {
    nextCanvasStages = canvasStages.map((stage, index) => applySavedStage(stage, sortedSaved[index]!));
  } else {
    const usedSaved = new Set<string>();
    nextCanvasStages = canvasStages.map((stage) => {
      const match = sortedSaved.find((saved, index) => {
        if (usedSaved.has(saved.stageId)) return false;
        const kind = savedStageKind(saved, index, sortedSaved.length);
        if (kind !== stage.kind) return false;
        if (kind === 'custom') {
          return saved.stageName.trim().toLowerCase() === stage.label.trim().toLowerCase();
        }
        return true;
      });
      if (!match) return stage;
      usedSaved.add(match.stageId);
      return applySavedStage(stage, match);
    });
  }

  return {
    canvasStages: nextCanvasStages,
    stageConfigs: remapStageConfigKeys(stageConfigs, stageIdMap),
    stageIdMap,
  };
}

export function buildSaveWorkflowTemplatePayload(input: SaveWorkflowTemplateInput) {
  const isUpdate = Boolean(input.workflowTemplateId?.trim());
  const customStages = input.canvasStages.filter((s) => s.kind === 'custom');

  const custom_stages = customStages.map((s) => {
    const canvasIndex = input.canvasStages.findIndex((stage) => stage.id === s.id);
    const stage_name = s.label.trim();
    const stage_order = canvasIndex >= 0 ? canvasIndex : 0;
    if (isUpdate) {
      return { stage_name, stage_order };
    }
    return {
      client_stage_key: s.id,
      stage_name,
      stage_order,
    };
  });

  const configurableStages = input.canvasStages.filter(
    (s) => s.kind === 'custom' || s.kind === 'closure' || s.kind === 'allocation' || s.kind === 'return',
  );

  const stage_configurations = configurableStages
    .map((stage) => {
      const cfg = input.stageConfigs[stage.id];
      if (!cfg) return null;

      const hasRouting = cfg.routingConfigured && cfg.routing;
      const hasKpis = cfg.kpiConfigured && cfg.kpis;
      const hasEscalation = cfg.escalationConfigured && cfg.escalation;
      const hasAssignment = stage.kind === 'closure' && typeof cfg.closureRoleId === 'number';

      if (!hasRouting && !hasKpis && !hasEscalation && !hasAssignment) return null;

      const entry: Record<string, unknown> = { stage_name: stage.label.trim() };
      // const entry: Record<string, unknown> = {  };
      if (hasRouting) {
        entry.routing_rules = {
          default_next_stage_name: nextStageName(input.canvasStages, stage.id),
          allowed_revert_stage_names: revertKeysToStageNames(
            input.canvasStages,
            cfg.routing!.allowedRevertStageKeys,
          ),
        };
      }

      if (hasKpis) {
        const k = cfg.kpis!;
        entry.kpis = {
          ttc: kpiNumberForSave(k.ttc),
          ttc_time_unit_id: kpiUnitIdForSave(k.ttc, k.ttcTimeUnitId),
          ttr: kpiNumberForSave(k.ttr),
          ttr_time_unit_id: kpiUnitIdForSave(k.ttr, k.ttrTimeUnitId),
          tte: kpiNumberForSave(k.tte),
          tte_time_unit_id: kpiUnitIdForSave(k.tte, k.tteTimeUnitId),
        };
      }

      if (hasEscalation) {
        entry.escalation_rules = {
          escalation_recipient_user_ids: cfg.escalation!.escalationRecipientUserIds,
        };
      }

      if (hasAssignment) {
        entry.assignment = { role_id: cfg.closureRoleId };
      }

      return entry;
    })
    .filter((x): x is Record<string, unknown> => Boolean(x));

  const letterTemplateIds = (input.letterTemplateIds ?? []).map((id) => id.trim()).filter(Boolean);

  const body: Record<string, unknown> = {
    workflow_template_name: input.workflowTemplateName.trim(),
    file_category_id: input.fileCategoryId,
    sub_file_category_id: input.subFileCategoryId?.trim() || null,
    allocation_officer_id: input.allocationOfficerId,
    letter_template_ids: letterTemplateIds,
    letter_template_id: null,
    is_draft_status: input.isDraft,
    confirm_replace_active: input.confirmReplaceActive ?? false,
    custom_stages,
    stage_configurations,
  };

  if (input.workflowTemplateId) body.workflow_template_id = input.workflowTemplateId;
  if (input.workflowCode?.trim()) body.workflow_code = input.workflowCode.trim();

  return body;
}

/** GET allocation officers for workflow template designer */
export async function getAllocationOfficers(input?: {
  signal?: AbortSignal;
}): Promise<ApiResult<AllocationOfficerOption[]>> {
  const applicationCode = workflowAppCode();
  const res = await caseApiRequest<unknown>('/users/allocation-officers', {
    method: 'GET',
    signal: input?.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  const data = normalizeAllocationOfficers(res.data);
  return { success: true, data };
}

function normalizeLatestWorkflowVersion(raw: unknown): LatestWorkflowVersionResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const versionRaw = r.version_no ?? r.versionNo;
  let versionNo: number | null = null;
  if (versionRaw === null || versionRaw === undefined) {
    versionNo = null;
  } else if (typeof versionRaw === 'number' && Number.isFinite(versionRaw)) {
    versionNo = versionRaw;
  } else if (typeof versionRaw === 'string' && versionRaw.trim()) {
    const parsed = Number(versionRaw);
    versionNo = Number.isFinite(parsed) ? parsed : null;
  }

  return {
    subFileCategoryId: strField(r, 'sub_file_category_id', 'subFileCategoryId'),
    workflowTemplateName: strField(r, 'workflow_template_name', 'workflowTemplateName'),
    versionNo,
  };
}

/** GET workflow template by id for designer edit/view */
export async function getWorkflowTemplateById(
  id: string,
  input?: { signal?: AbortSignal },
): Promise<ApiResult<WorkflowTemplateDesignerLoadResult>> {
  const templateId = id.trim();
  if (!templateId) return { success: false, message: 'Template id is required.' };

  const res = await caseApiRequest<unknown>(
    `/v1/workflow-templates/workflow-template/${encodeURIComponent(templateId)}`,
    { method: 'GET', signal: input?.signal },
  );
  if (!res.ok) return { success: false, message: res.message };

  const data = normalizeWorkflowTemplateDesignerLoad(res.data);
  if (!data) return { success: false, message: 'Workflow template response is invalid.' };
  return { success: true, data };
}

/** GET workflow templates for a sub file category (used to derive workflow code sequence) */
export type WorkflowTemplateDetailItem = {
  id: string;
  status: 'Draft' | 'Active' | 'Inactive';
  workflowCode?: string | null;
  versionNo?: number | null;
};

function normalizeWorkflowTemplateDetailItem(raw: unknown): WorkflowTemplateDetailItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = strField(r, 'id', 'workflowTemplateId', 'workflow_template_id');
  if (!id) return null;

  let statusRaw = strField(r, 'status', 'statusName', 'status_name');
  if (!statusRaw && r.status && typeof r.status === 'object') {
    statusRaw = nestedName(r.status);
  }

  const workflowCodeRaw = strField(r, 'code', 'workflow_code', 'workflowCode');
  const versionNo = numField(r, 'version', 'versionNo', 'version_no') ?? null;

  return {
    id,
    status: normalizeWorkflowStatusName(statusRaw || 'Draft'),
    workflowCode: workflowCodeRaw || null,
    versionNo,
  };
}

function normalizeWorkflowTemplateDetails(data: unknown): WorkflowTemplateDetailItem[] {
  return asArray(data)
    .map(normalizeWorkflowTemplateDetailItem)
    .filter((item): item is WorkflowTemplateDetailItem => Boolean(item));
}

/** Returns the first active template for a sub category, optionally excluding the current template. */
export function findActiveWorkflowTemplateInDetails(
  items: WorkflowTemplateDetailItem[],
  excludeTemplateId?: string,
): WorkflowTemplateDetailItem | null {
  const exclude = excludeTemplateId?.trim();
  for (const item of items) {
    if (item.status !== 'Active') continue;
    if (exclude && item.id === exclude) continue;
    return item;
  }
  return null;
}

export async function getWorkflowTemplateDetails(
  subFileCategoryId: string,
  input?: { signal?: AbortSignal },
): Promise<ApiResult<WorkflowTemplateDetailItem[]>> {
  const id = subFileCategoryId.trim();
  if (!id) return { success: true, data: [] };

  const applicationCode = workflowDetailsAppCode();
  const res = await workflowApiRequest<unknown>(`/details/${encodeURIComponent(id)}`, {
    method: 'GET',
    signal: input?.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeWorkflowTemplateDetails(res.data) };
}

/** GET latest version number for a template name within a sub file category */
export async function getLatestWorkflowVersion(
  subFileCategoryId: string,
  workflowTemplateName: string,
  input?: { signal?: AbortSignal },
): Promise<ApiResult<LatestWorkflowVersionResult>> {
  const subId = subFileCategoryId.trim();
  const name = workflowTemplateName.trim();
  if (!subId || !name) {
    return {
      success: true,
      data: { subFileCategoryId: subId, workflowTemplateName: name, versionNo: null },
    };
  }

  const applicationCode = workflowAppCode();
  const qs = new URLSearchParams({
    sub_file_category_id: subId,
    workflow_template_name: name,
  });
  const res = await workflowApiRequest<unknown>(`/latest-version?${qs.toString()}`, {
    method: 'GET',
    signal: input?.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };

  const data = normalizeLatestWorkflowVersion(res.data);
  if (!data) return { success: false, message: 'Latest version response is invalid.' };
  return { success: true, data };
}

/** GET letter templates for a sub file category */
export async function getLetterTemplates(
  subCategoryId: string,
  options?: { signal?: AbortSignal },
): Promise<ApiResult<LetterTemplateOption[]>> {
  const id = subCategoryId.trim();
  if (!id) return { success: true, data: [] };

  const applicationCode = workflowAppCode();
  const qs = new URLSearchParams({ sub_category_id: id });
  const res = await workflowApiRequest<unknown>(`/letter-templates?${qs.toString()}`, {
    method: 'GET',
    signal: options?.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeLetterTemplates(res.data) };
}

/** Save workflow template — create (omit workflow_template_id) or update draft */
export async function saveWorkflowTemplate(
  input: SaveWorkflowTemplateInput,
): Promise<SaveWorkflowTemplateApiResult> {
  const body = buildSaveWorkflowTemplatePayload(input);
  const applicationCode = workflowAppCode();

  const res = await workflowApiRequest<unknown>('', {
    method: 'POST',
    body,
    headers: { applicationCode, 'application-code': applicationCode },
  });

  if (!res.ok) {
    return {
      success: false,
      message: res.message,
      needsConfirmReplace: res.status === 409,
    };
  }

  const data = normalizeSaveWorkflowResponse(res.data);
  if (!data) {
    return { success: false, message: 'Workflow template response is invalid.' };
  }

  return { success: true, data };
}

/** CMS-WFT-6 — in-place Allocation Officer update on an Active template (is_draft_status=true). */
export async function saveActiveTemplateAllocationOfficer(
  input: SaveWorkflowTemplateInput,
): Promise<SaveWorkflowTemplateApiResult> {
  const expectedTemplateId = input.workflowTemplateId?.trim();
  if (!expectedTemplateId) {
    return { success: false, message: '', allocationOfficerSaveFailed: true };
  }

  const res = await saveWorkflowTemplate({
    ...input,
    workflowCode: undefined,
    isDraft: true,
    confirmReplaceActive: false,
  });

  if (!res.success) {
    return { success: false, message: '', allocationOfficerSaveFailed: true };
  }

  const { data } = res;
  const statusCode = (data.statusCode ?? data.status ?? '').trim().toUpperCase();
  const unexpectedResponse =
    data.isNewVersion === true ||
    data.isNewTemplate === true ||
    statusCode === 'DRAFT' ||
    data.workflowTemplateId !== expectedTemplateId;

  if (unexpectedResponse) {
    return { success: false, message: '', allocationOfficerSaveFailed: true };
  }

  return res;
}

const WORKFLOW_VALIDATION_ACTION_LABELS: Record<string, string> = {
  WORKFLOW_TEMPLATE_NAME: 'Go to Template Name',
  WORKFLOW_CODE: 'Go to Workflow Code',
  FILE_CATEGORY: 'Go to File Category',
  SUB_FILE_CATEGORY: 'Go to Sub File Category',
  ALLOCATION_OFFICER: 'Go to Allocation Officer',
  LETTER_TEMPLATE: 'Go to Letter Template',
  MIN_ONE_CUSTOM_STAGE: 'Go to Canvas',
  ROUTING_RULES: 'Configure Routing Rules',
  KPIS: 'Configure KPIs',
  ESCALATION_RULES: 'Configure Escalation Rules',
  CLOSURE_ASSIGNMENT: 'Configure Assignment',
};

function isValidationCheckPassed(item: Record<string, unknown>): boolean {
  if (item.passed === true || item.valid === true || item.is_valid === true || item.success === true) return true;
  if (item.passed === false || item.valid === false || item.is_valid === false || item.success === false) return false;
  const status = strField(item, 'status', 'result', 'validation_status', 'validationStatus').toLowerCase();
  if (status === 'passed' || status === 'pass' || status === 'success' || status === 'valid' || status === 'ok') {
    return true;
  }
  if (status === 'failed' || status === 'fail' || status === 'invalid' || status === 'error') return false;
  return false;
}

function validationDetailLabel(item: Record<string, unknown>): string {
  const details = asArray(item.details);
  const texts = details
    .map((d) => (typeof d === 'string' ? d.trim() : ''))
    .filter((d): d is string => Boolean(d));
  if (texts.length) return texts.join(' ');
  return strField(item, 'label', 'description', 'message') || strField(item, 'rule_code', 'ruleCode');
}

function parseValidationResultRow(item: Record<string, unknown>): WorkflowValidationCheck | null {
  const key = strField(item, 'rule_code', 'ruleCode');
  if (!key) return null;
  return {
    key,
    label: validationDetailLabel(item),
    passed: isValidationCheckPassed(item),
    actionLabel: WORKFLOW_VALIDATION_ACTION_LABELS[key],
  };
}

function extractValidationCheckRows(raw: unknown): Record<string, unknown>[] {
  if (!raw || typeof raw !== 'object') return [];
  const r = raw as Record<string, unknown>;
  const candidates = [r.validation_results, r.validationResults, r.validation_checks, r.validationChecks];
  for (const candidate of candidates) {
    const rows = asArray(candidate);
    if (rows.length) {
      return rows.filter((x): x is Record<string, unknown> => Boolean(x && typeof x === 'object'));
    }
  }
  return [];
}

export function buildClientWorkflowValidationChecks(input: SaveWorkflowTemplateInput): WorkflowValidationCheck[] {
  const customStages = input.canvasStages.filter((s) => s.kind === 'custom');
  const closureStage = input.canvasStages.find((s) => s.kind === 'closure');
  const closureAssignmentOk = closureStage
    ? typeof input.stageConfigs[closureStage.id]?.closureRoleId === 'number' &&
      (input.stageConfigs[closureStage.id]?.closureRoleId ?? 0) > 0
    : false;

  // Exempt categories (RTI, Public Petition) have no sub-categories;
  // treat the sub-category check as passed when the flag is set.
  const subCategoryOk = Boolean(input.subFileCategoryId) || Boolean(input.isExemptCategory);

  const checks: WorkflowValidationCheck[] = [
    {
      key: 'WORKFLOW_TEMPLATE_NAME',
      label: 'Workflow Template Name entered.',
      passed: Boolean(input.workflowTemplateName.trim()),
      actionLabel: WORKFLOW_VALIDATION_ACTION_LABELS.WORKFLOW_TEMPLATE_NAME,
    },
    {
      key: 'FILE_CATEGORY',
      label: 'File Category selected.',
      passed: Boolean(input.fileCategoryId),
      actionLabel: WORKFLOW_VALIDATION_ACTION_LABELS.FILE_CATEGORY,
    },
    {
      key: 'SUB_FILE_CATEGORY',
      label: 'Sub File Category selected.',
      passed: subCategoryOk,
      actionLabel: WORKFLOW_VALIDATION_ACTION_LABELS.SUB_FILE_CATEGORY,
    },
    {
      key: 'ALLOCATION_OFFICER',
      label: 'Assigned Allocation Officer selected.',
      passed: Boolean(input.allocationOfficerId),
      actionLabel: WORKFLOW_VALIDATION_ACTION_LABELS.ALLOCATION_OFFICER,
    },
    {
      key: 'LETTER_TEMPLATE',
      label: 'Letter Template selected.',
      passed: (input.letterTemplateIds ?? []).some((id) => id.trim()),
      actionLabel: WORKFLOW_VALIDATION_ACTION_LABELS.LETTER_TEMPLATE,
    },
    {
      key: 'MIN_ONE_CUSTOM_STAGE',
      label:
        "At least one stage added in between Start and End nodes, except for the 'Allocation Officer Assignment', 'Closure' and 'Return to Allocation Officer' fixed stages.",
      passed: customStages.length >= 1,
      actionLabel: WORKFLOW_VALIDATION_ACTION_LABELS.MIN_ONE_CUSTOM_STAGE,
    },
    {
      key: 'CLOSURE_ASSIGNMENT',
      label: 'Assignment for Closure configured.',
      passed: closureAssignmentOk,
      actionLabel: WORKFLOW_VALIDATION_ACTION_LABELS.CLOSURE_ASSIGNMENT,
    },
  ];

  return checks;
}
export function normalizeWorkflowValidationResponse(
  raw: unknown,
  fallbackChecks: WorkflowValidationCheck[],
): WorkflowValidationResult {
  const rows = extractValidationCheckRows(raw);
  if (!rows.length) {
    const valid = fallbackChecks.every((c) => c.passed);
    return { valid, checks: fallbackChecks };
  }

  const checks = rows
    .map((item) => parseValidationResultRow(item))
    .filter((x): x is WorkflowValidationCheck => Boolean(x));

  let valid = false;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    const explicitValid = r.is_valid ?? r.isValid ?? r.valid ?? r.success;
    if (typeof explicitValid === 'boolean') {
      valid = explicitValid;
    } else if (checks.length > 0) {
      valid = checks.every((c) => c.passed);
    }
  } else if (checks.length > 0) {
    valid = checks.every((c) => c.passed);
  }

  let workflowTemplateId: string | undefined;
  let workflowCode: string | undefined;
  let versionNo: string | number | undefined;
  let requiresActiveReplacementConfirmation: boolean | undefined;
  let isNewTemplate: boolean | undefined;

  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    const isNewTemplateRaw = r.is_new_template ?? r.isNewTemplate;
    if (typeof isNewTemplateRaw === 'boolean') isNewTemplate = isNewTemplateRaw;

    if (isNewTemplate !== true) {
      workflowTemplateId =
        strField(r, 'workflow_template_id', 'workflowTemplateId', 'fe_version_id', 'feVersionId') || undefined;
    }
    workflowCode = strField(r, 'workflow_code', 'workflowCode', 'code') || undefined;
    const versionRaw = r.version_no ?? r.versionNo;
    if (typeof versionRaw === 'number' || typeof versionRaw === 'string') versionNo = versionRaw;
    const replaceRaw = r.requires_active_replacement_confirmation ?? r.requiresActiveReplacementConfirmation;
    if (typeof replaceRaw === 'boolean') requiresActiveReplacementConfirmation = replaceRaw;
  }

  return {
    valid,
    checks,
    workflowTemplateId,
    workflowCode,
    versionNo,
    requiresActiveReplacementConfirmation,
    isNewTemplate,
  };
}

/** POST validate workflow template before activation */
export async function validateWorkflowTemplate(
  input: SaveWorkflowTemplateInput,
): Promise<ApiResult<WorkflowValidationResult>> {
  const fallbackChecks = buildClientWorkflowValidationChecks(input);
  const body = buildSaveWorkflowTemplatePayload({
    ...input,
    confirmReplaceActive: false,
  });
  const applicationCode = workflowAppCode();

  const res = await workflowApiRequest<unknown>('/validate', {
    method: 'POST',
    body,
    headers: { applicationCode, 'application-code': applicationCode },
  });

  if (!res.ok) {
    return { success: false, message: res.message };
  }

  const data = normalizeWorkflowValidationResponse(res.data, fallbackChecks);
  return { success: true, data };
}

export type WorkflowTemplateListItem = {
  id: string;
  workflowCode: string;
  workflowTemplateName: string;
  fileCategory: string;
  subFileCategory: string;
  version: number;
  status: string;
  allocationOfficerName: string;
  updatedByName: string;
  updatedAt: string;
};

export type WorkflowTemplateListPage = {
  items: WorkflowTemplateListItem[];
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
};

export async function fetchWorkflowTemplatesList(params: {
  pageNumber: number;
  pageSize: number;
  workflowTemplateName?: string;
  fileCategory?: string;
  subFileCategory?: string;
  status?: string;
  allocationOfficerName?: string;
  sortBy?: string;
  sortDirection?: string;
}): Promise<ApiResult<WorkflowTemplateListPage>> {
  const qs = new URLSearchParams({
    pageNumber: String(params.pageNumber),
    pageSize: String(params.pageSize),
  });

  const body: Record<string, string> = {};
  if (params.workflowTemplateName) body.workflowTemplateName = params.workflowTemplateName;
  if (params.fileCategory) body.fileCategory = params.fileCategory;
  if (params.subFileCategory) body.subFileCategory = params.subFileCategory;
  if (params.status) body.status = params.status === 'Inactive' ? 'Submitted' : params.status;
  if (params.allocationOfficerName) body.allocationOfficerName = params.allocationOfficerName;
  if (params.sortBy) body.sortBy = params.sortBy;
  if (params.sortDirection) body.sortDirection = params.sortDirection;

  type RawItem = {
    id: string;
    workflow_code: string;
    workflow_template_name: string;
    fileCategory?: string;
    subFileCategory?: string;
    version: number;
    status: string;
    allocation_officer_name?: string;
    updated_by_name?: string;
    updated_at?: string;
  };

  type SpringPage = {
    content: RawItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  };

  const res = await caseApiRequest<SpringPage>(
    `/v1/workflow-templates/workflow-templates-list?${qs.toString()}`,
    { method: 'POST', body },
  );

  if (!res.ok) return { success: false, message: res.message };

  return {
    success: true,
    data: {
      items: res.data.content.map((item) => ({
        id: item.id,
        workflowCode: item.workflow_code,
        workflowTemplateName: item.workflow_template_name,
        fileCategory: item.fileCategory ?? '',
        subFileCategory: item.subFileCategory ?? '',
        version: item.version,
        status: item.status === 'Submitted' ? 'Inactive' : item.status,
        allocationOfficerName: item.allocation_officer_name ?? '',
        updatedByName: item.updated_by_name ?? '',
        updatedAt: item.updated_at ?? '',
      })),
      totalElements: res.data.totalElements,
      totalPages: res.data.totalPages,
      pageNumber: res.data.number,
      pageSize: res.data.size,
    },
  };
}

export async function deleteWorkflowTemplate(id: string): Promise<ApiResult<void>> {
  const res = await caseApiRequest<void>(`/v1/workflow-templates/${id}`, { method: 'DELETE' });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

function normalizeStageLabel(label: string): string {
  return label.trim().toLowerCase();
}

function matchSourceStageForDuplicate(
  sourceStages: DesignerCanvasStage[],
  duplicateStage: DesignerCanvasStage,
  duplicateIndex: number,
): DesignerCanvasStage | undefined {
  const byLabel = sourceStages.find(
    (stage) =>
      stage.kind === duplicateStage.kind &&
      normalizeStageLabel(stage.label) === normalizeStageLabel(duplicateStage.label),
  );
  if (byLabel) return byLabel;
  const byIndex = sourceStages[duplicateIndex];
  return byIndex?.kind === duplicateStage.kind ? byIndex : undefined;
}

/** Remap routing/KPI/escalation configs from a source template onto duplicated stage ids. */
export function remapStageConfigsForDuplicate(
  source: WorkflowTemplateDesignerLoadResult,
  duplicate: WorkflowTemplateDesignerLoadResult,
): Record<string, WorkflowStageConfigState> {
  const stageIdMap = new Map<string, string>();
  duplicate.canvasStages.forEach((duplicateStage, index) => {
    const sourceStage = matchSourceStageForDuplicate(source.canvasStages, duplicateStage, index);
    if (sourceStage) stageIdMap.set(sourceStage.id, duplicateStage.id);
  });

  const remapped: Record<string, WorkflowStageConfigState> = {};
  for (const [index, duplicateStage] of duplicate.canvasStages.entries()) {
    const sourceStage = matchSourceStageForDuplicate(source.canvasStages, duplicateStage, index);
    const emptyConfig: WorkflowStageConfigState = {
      routingConfigured: false,
      kpiConfigured: false,
      escalationConfigured: false,
    };
    if (!sourceStage) {
      remapped[duplicateStage.id] = duplicate.stageConfigs[duplicateStage.id] ?? emptyConfig;
      continue;
    }

    const sourceConfig = source.stageConfigs[sourceStage.id];
    if (!sourceConfig) {
      remapped[duplicateStage.id] = duplicate.stageConfigs[duplicateStage.id] ?? emptyConfig;
      continue;
    }

    const nextConfig: WorkflowStageConfigState = {
      ...sourceConfig,
      routing: sourceConfig.routing
        ? {
            allowedRevertStageKeys: sourceConfig.routing.allowedRevertStageKeys.map(
              (key) => stageIdMap.get(key) ?? key,
            ),
          }
        : undefined,
    };
    remapped[duplicateStage.id] = nextConfig;
  }

  return remapped;
}

/** Copy stage configurations from the source template onto a duplicated draft. */
export async function hydrateDuplicatedWorkflowTemplate(
  sourceTemplateId: string,
  duplicateTemplateId: string,
): Promise<ApiResult<void>> {
  const [sourceRes, duplicateRes] = await Promise.all([
    getWorkflowTemplateById(sourceTemplateId),
    getWorkflowTemplateById(duplicateTemplateId),
  ]);
  if (!sourceRes.success) return { success: false, message: sourceRes.message };
  if (!duplicateRes.success) return { success: false, message: duplicateRes.message };

  const source = sourceRes.data;
  const duplicate = duplicateRes.data;
  const stageConfigs = remapStageConfigsForDuplicate(source, duplicate);

  const saveRes = await saveWorkflowTemplate({
    workflowTemplateId: duplicate.workflowTemplateId,
    workflowCode: duplicate.workflowCode.trim() || undefined,
    workflowTemplateName: duplicate.workflowTemplateName,
    fileCategoryId: duplicate.fileCategoryId,
    subFileCategoryId: duplicate.subFileCategoryId,
    isExemptCategory: !duplicate.subFileCategoryId.trim() && Boolean(duplicate.fileCategoryId),
    allocationOfficerId: duplicate.allocationOfficerId,
    letterTemplateIds: duplicate.letterTemplateIds,
    isDraft: true,
    confirmReplaceActive: false,
    canvasStages: duplicate.canvasStages,
    stageConfigs,
  });

  if (!saveRes.success) return { success: false, message: saveRes.message };
  return { success: true, data: undefined };
}

export async function duplicateWorkflowTemplate(id: string): Promise<ApiResult<{ id: string }>> {
  const res = await caseApiRequest<{ id: string }>(`/v1/workflow-templates/${id}/duplicate`, {
    method: 'POST',
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}